import { Filter } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import {
  formatNumber,
  NumberFormatPreset,
  QuickViewReferenceValue,
  QuickViewWidget,
  useScrollIntoView,
} from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';
import { DimPurchaseOrderField } from '~/common/types/cubes/DimPuchaseOrder';
import { useDateRange } from '~/components/DateRangeProvider';
import { convertDateRangeToTimeDimension } from '~/util/cube';
import { getPrimaryValue } from '~/util/cube/getPrimaryValue';

interface PurchaseOrderVolumeShareWidgetProps {
  filters: Filter[];
}
const formatChange = formatNumber(NumberFormatPreset.PERCENT_DECIMAL);
const formatValue = formatNumber(NumberFormatPreset.CURRENCY_COMPACT);
const WIDGET = 'PurchaseOrderVolumeShareWidget';

export const PurchaseOrderVolumeShareWidget = ({ filters }: PurchaseOrderVolumeShareWidgetProps) => {
  const { t } = useTranslation(I18nNamespace.base, { keyPrefix: WIDGET });
  const [widgetRef, active] = useScrollIntoView({ hash: 'purchase-order-volume-share' });
  const { currentDateRange, referenceDateRange } = useDateRange();

  const measure = DimPurchaseOrderField.TOTAL_VALUE_NORMALIZED;

  const { resultSet: resultSetCurrent, isLoading: loadingCurrent } = useCubeQuery({
    measures: [measure],
    filters,
    timeDimensions: convertDateRangeToTimeDimension(DimPurchaseOrderField.CREATED_AT, currentDateRange),
  });

  const { resultSet: resultSetPrevious, isLoading: loadingPrevious } = useCubeQuery({
    measures: [measure],
    timeDimensions: convertDateRangeToTimeDimension(DimPurchaseOrderField.CREATED_AT, referenceDateRange),
  });

  const currentValue = getPrimaryValue(resultSetCurrent, measure) ?? 0;
  const previousValue = getPrimaryValue(resultSetPrevious, measure) ?? 0;
  const changeRate = currentValue / previousValue;
  return (
    <QuickViewWidget
      ref={widgetRef}
      active={active}
      loading={loadingCurrent || loadingPrevious}
      title={t('title')}
      subTitle={t('subTitle')}
      value={formatChange(changeRate)}
      valueTooltip={t('valueTooltip')}
      data-intercom-target={WIDGET}
      referenceValue={
        previousValue && (
          <QuickViewReferenceValue description={formatValue(previousValue)} value={formatValue(currentValue)} />
        )
      }
    />
  );
};
